import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import PayChallanModal from "../admission/PayChallanModal";
import { customTableStyles } from "../../assets/CustomTableStyles";
import FullScreenLoader from "../../components/FullScreenLoader";
import jsPDF from "jspdf";
import "jspdf-autotable";
import IISSLogo from "../../assets/img/logo-full.png";
import useAdmissionChallanPdf from "../../hooks/useAdmissionChallanPdf";
import useBillingChallanPdf from "../../hooks/useBillingChallanPdf";

function MonthlyBilling() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [triggerRerender, setTriggerRerender] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [processing, setProcessing] = useState(false);

  const {
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      centerClassId: "",
      centerId: "",
      comments: "",
      feeFrequency: null,
      userId: localStorage.getItem("userId"),
    },
  });
  const sessionId = watch("sessionId");
  const feeFrequency = watch("feeFrequency");

  const [selectedCenterId, setSelectedCenterId] = useState(
    savedCenterId || null
  );
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [sections, setSections] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [feeFrequencyData, setFeeFrequencyData] = useState([]);
  const [billingStudent, setBillingStudent] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  // const { generatePDF, loading } = useBillingChallanPdf();
  const [sessionPeriods, setSessionPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  const centerClassId = watch("centerClassId");
  var sessionPeriod = watch("sessionPeriod");

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response);
        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
  }, [setValue, triggerRerender, savedCenterId]);

  const fetchClassesByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setClassesData(response);
      } else {
        setClassesData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    getSessionsList();

    if (selectedCenterId) {
      fetchClassesByCenterId(selectedCenterId);
      getAllStudentByClassIdAndCenterId();
    }

    if (selectedClassId) {
      getAllStudentByClassIdAndCenterId();
    }
  }, [selectedCenterId, selectedClassId, selectedSectionId, selectedPeriodId]);

  const getSessionsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `FeePlan/GetCenterSessionsByCenterId?centerId=${savedCenterId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSessionsData(response.centerSessions);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };

  useEffect(() => {
    if (sessionsData.length > 0 && !selectedSessionId) {
      const firstSession = sessionsData[0];
      setValue("sessionId", {
        value: firstSession.id,
        label: firstSession.sessionName,
      });
      setSelectedSessionId(firstSession.id);
    }
  }, [sessionsData, selectedSessionId, setValue]);

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    fetchCommonSettings("feeFrequency", setFeeFrequencyData);
  }, [token]);

  // NEW EFFECT: Set first fee frequency option as default when data is loaded
  useEffect(() => {
    if (feeFrequencyData.length > 0 && !feeFrequency) {
      const firstFrequency = feeFrequencyData[0];
      const frequencyOption = {
        value: firstFrequency.id,
        label: firstFrequency.value,
      };
      setValue("feeFrequency", frequencyOption);
    }
  }, [feeFrequencyData, feeFrequency, setValue]);

  const getAllClassSectionsByClassIdAndCenterId = async (classId, centerId) => {
    try {
      if (classId && centerId) {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${classId}&centerId=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSections(response);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  const getAllStudentByClassIdAndCenterId = async () => {
    setFetching(true);
    try {
      const response = await get(
        `StudentChallan/GetAllRegistrationByClassIdAndMonth?centerId=${selectedCenterId}&ClassId=${
          selectedClassId || 0
        }&sectionId=${selectedSectionId || 0}&sessionPeriodId=${
          selectedPeriodId || 0
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBillingStudent(response.result);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setFetching(false);
    }
  };

  const handlePayChallanClick = async (row) => {
    try {
      const confirmProcessing = window.confirm(
        "Are you sure you want to pay the challan?"
      );
      if (!confirmProcessing) {
        return;
      }
      const response = await get(
        `StudentChallan/GetAdmissionVoucherByChallanIdAsync?challanId=${row.challanId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        setModalData(response?.admissionVoucher[0]);
        setShowModal(true);
      } else {
        console.error("Error fetching fee details");
      }
    } catch (error) {
      console.error("Error fetching admission voucher:", error);
    } finally {
    }
  };

  useEffect(() => {
    const fetchSections = async () => {
      if (!selectedCenterId || !centerClassId?.value) {
        setSections([]);
        setValue("sectionId", null);
        setSelectedSectionId(null);
        return;
      }

      try {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${centerClassId.value}&centerId=${selectedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSections(response || []);
      } catch (error) {
        console.error("Error fetching sections:", error);
        setSections([]);
      }
    };

    fetchSections();
  }, [selectedCenterId, centerClassId?.value, token, setValue]);

  useEffect(() => {
    const fetchSessionPeriods = async () => {
      if (!selectedSessionId) {
        setSessionPeriods([]);
        setValue("sessionPeriod", null);
        setSelectedPeriodId(null);
        return;
      }

      try {
        const response = await get(
          `Session/GetSessionPeriodBySessionId?sessionId=${selectedSessionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSessionPeriods(response.session || []);
      } catch (error) {
        console.error("Error fetching session periods:", error);
      }
    };

    fetchSessionPeriods();
  }, [selectedSessionId, token, setValue]);

  const processStudents = async (studentId = null) => {
    const centerId = selectedCenterId;
    const classId = selectedClassId;
    if (!selectedPeriodId) {
      toast.error("Session Period Is Required!");
      return false;
    }
    const confirmProcessing = window.confirm(
      "Are you sure you want to process the selected students?"
    );
    if (!confirmProcessing) {
      return;
    }
    const studentIds = studentId
      ? [studentId]
      : billingStudent.map((student) => student.id);

    const formData = {
      centerId: selectedCenterId,
      studentIds: studentIds,
      installmentId: selectedPeriodId,
      feeFrequencyId: feeFrequency.value,
      centerSessionId: sessionId.value,
      createdBy: localStorage.getItem("userId"),
    };
    setProcessing(true);
    try {
      const response = await post(
        "StudentChallan/MonthlyProcessing",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Processing successful!");
        getAllStudentByClassIdAndCenterId();
      }
    } catch (error) {
      console.error("Error processing students:", error);
    } finally {
      setProcessing(false);
    }
  };

  // Base64 placeholder logo (same as before)
  const placeholderLogo =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAIAAAD/gAIDAAAACXBIWXMAAAsTAAALEwEAmpwYAAABJklEQVR4nO3TsQ3AMAwAwfxf6WADYgMLcQAFa4AJa4AJ0zM+7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozv7DwAALB0i1O1VHrUAAAAASUVORK5CYII=";

  const handlePrintChallanAction = async (row) => {
    if (!row.challanId) {
      toast.error("Error: Challan ID is missing.");
      return;
    }



    try {
      setLoading(true);

      const response = await get(
        `StudentChallan/GetMonthlyBillingChallanByChallanId?studentFirstRegistrationId=${row.id}&installmentId=${sessionPeriod.value}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.isSuccess || !response.centerFeePlans) {
        throw new Error(response.message || "Failed to fetch challan data");
      }
      
      // Custom Voucher Logic for Center 2076
      if (String(selectedCenterId) === "2076") {
        const feeItems = response.centerFeePlans;
        const totalAmount = feeItems.reduce((sum, item) => sum + (item.amount || 0), 0);
        
        // Format dates
        const formatDate = (dateString) => {
          if (!dateString) return "";
          const date = new Date(dateString);
          return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        };

        const centerLogo = centersData.find(c => String(c.id) === "2076")?.imagePath;
        const apiLogo = feeItems[0]?.centerImagePath;
        const finalLogo = apiLogo 
          ? `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${apiLogo}` 
          : centerLogo 
            ? `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogo}` 
            : "";

        const commonData = {
          acNo: feeItems[0]?.code || "1234567890", // Use API code or fallback
          className: feeItems[0]?.className || "",
          feeMonth: row.challanMonth || feeItems[0]?.feeCycleName || "", 
          studentName: row.name || "",
          fatherName: feeItems[0]?.fatherName || row.fatherName || "", // Fallback to row if not in items
          contact: "0345-7586677", 
          dueDate: formatDate(feeItems[0]?.dueDate),
          totalDue: totalAmount.toFixed(0),
          totalAfter: (totalAmount + 100).toFixed(0),
          
          // Fee Mappings
          feeTypeName: feeItems[0]?.challanMonth || feeItems[0]?.feeTypeName || "",
          admissionFee: feeItems.find(i => i.feeTypeName.toLowerCase().includes("admission"))?.amount.toFixed(0) || "0",
          tutionFee: feeItems.find(i => i.feeTypeName.toLowerCase().includes("tution") || i.feeTypeName.toLowerCase().includes("tuition"))?.amount.toFixed(0) || "0",
          regFee: feeItems.find(i => i.feeTypeName.toLowerCase().includes("registration"))?.amount.toFixed(0) || "0",
          
          annualStationery: feeItems.find(i => i.feeTypeName.toLowerCase().includes("annual"))?.amount.toFixed(0) || "0",
          acCharges: feeItems.find(i => i.feeTypeName.toLowerCase().includes("ac charges"))?.amount.toFixed(0) || "0",
          sportsFee: feeItems.find(i => i.feeTypeName.toLowerCase().includes("sports"))?.amount.toFixed(0) || "0",
          
          others: feeItems.filter(i => !["admission", "tution", "tuition", "registration", "ac charges", "sports", "annual", "arrears"].some(t => i.feeTypeName.toLowerCase().includes(t)))
                           .reduce((sum, i) => sum + i.amount, 0).toFixed(0),
                           
          balance: feeItems.filter(i => i.feeTypeName.toLowerCase().includes("arrears")).reduce((sum, i) => sum + i.amount, 0).toFixed(0),
          stationery: "0",
          fine: "0",
          paperFund: "0",
          transportFee: "0",
          other: "0",
          admissionVoucher: feeItems[0]?.challanNo || "",
          centerImagePath: finalLogo,
        };

        localStorage.setItem('customVoucherData', JSON.stringify(commonData));
        window.open('/monthly-fee-voucher', '_blank');
        setLoading(false);
        return;
      }

      const data = response;

      const feeItems = data.centerFeePlans;

      if (feeItems.length === 0) {
        throw new Error("No fee items found in challan");
      }

      // Calculate total
      const totalAmount = feeItems.reduce(
        (sum, item) => sum + (item.amount || 0),
        0
      );

      // Format dates (PK format: DD/MM/YYYY)
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      };

      const issueDate = formatDate(feeItems[0].issueDate); // 15/11/2025
      const dueDate = formatDate(feeItems[0].dueDate); // 22/11/2025

      // Prepare challan data
      const challanData = {
        items: feeItems.map((item) => ({
          headName: item.feeTypeName,
          headCode: item.code || "N/A",
          amount: item.amount,
          periodDesc: `${item.feeCycleName} (${item.feePlanName})`,
          waiveOfAmount: 0,
          challanNo: item.challanNo,
          centerName: "IISS",
          studentName: row.name || "Student Name",
          firstRegistrationId: row.id,
          className: item.className,
          challanDate: issueDate,
          dueDate: dueDate,
          totalAmount,
          nwaAccountNo: "1234567890",
          branchCode: "0001",
          branchName: "Main Branch",
          isPaid: false,
          baseUrl: "",
        })),
      };

      // Generate PDF
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const styles = {
        font: "helvetica",
        fontSize: 9.5,
        cellPadding: 2,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      };

      const copies = ["Bank Copy", "School Copy", "Student Copy"];
      let currentX = 20;

      // Load logo
      let logoImage = placeholderLogo;
      try {
        const logoResponse = await fetch(IISSLogo);
        if (logoResponse.ok) {
          const blob = await logoResponse.blob();
          logoImage = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        }
      } catch (error) {
        console.warn("Logo fetch failed, using placeholder:", error);
      }

      copies.forEach((copy) => {
        doc.addImage(logoImage, "PNG", currentX, 20, 100, 40);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.text(copy, currentX + 110, 40);

        // Challan No
        doc.autoTable({
          startY: 80,
          body: [
            [
              {
                content: `Challan #: ${challanData.items[0].challanNo}`,
                styles: { halign: "center" },
              },
            ],
          ],
          styles,
          margin: { left: currentX },
          tableWidth: 260,
        });

        // Info
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 10,
          body: [
            [
              {
                content: `IISS: ${challanData.items[0].centerName} Campus`,
                styles: { fontStyle: "bold" },
              },
            ],
            [
              {
                content: `Student Name: ${challanData.items[0].studentName}`,
                styles: { fontStyle: "bold" },
              },
            ],
            [
              {
                content: `Class: ${challanData.items[0].className}`,
                styles: { fontStyle: "bold" },
              },
            ],
            [
              {
                content: `Issue Date: ${issueDate}`,
                styles: { fontStyle: "bold" },
              },
            ],
            [
              {
                content: `Due Date: ${dueDate}`,
                styles: { fontStyle: "bold" },
              },
            ],
            [
              {
                content: "MONTHLY CHALLAN",
                styles: { halign: "center", fontStyle: "bold", fontSize: 11.5 },
              },
            ],
          ],
          styles: { ...styles, border: 0 },
          margin: { left: currentX },
          tableWidth: 260,
        });

        // Fee Items
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 10,
          head: [["Description", "Amount"]],
          body: challanData.items.map((item) => [
            `${item.headName}\n${item.periodDesc}`,
            { content: item.amount.toFixed(2), styles: { halign: "right" } },
          ]),
          styles,
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: "bold",
          },
          columnStyles: {
            0: { cellWidth: 180, fontStyle: "normal", fontSize: 8 },
            1: { cellWidth: 80, halign: "right" },
          },
          margin: { left: currentX },
          tableWidth: 260,
        });

        // Total
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 5,
          body: [
            [
              { content: "Total", styles: { fontStyle: "bold" } },
              {
                content: `${totalAmount.toFixed(2)}`,
                styles: { halign: "right" },
              },
            ],
            [
              { content: "Paid / Adjusted", styles: { fontStyle: "normal" } },
              { content: "", styles: { halign: "right" } },
            ],
            [
              {
                content: "Total Payment By Due Date",
                styles: { fontStyle: "bold" },
              },
              {
                content: `${totalAmount.toFixed(2)}`,
                styles: { halign: "right", fontStyle: "bold" },
              },
            ],
          ],
          styles: { ...styles, border: 0 },
          margin: { left: currentX },
          tableWidth: 260,
        });

        // Bank Details
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 30,
          body: [
            ["Account # Collection:", challanData.items[0].nwaAccountNo],
            ["Branch Code:", challanData.items[0].branchCode],
            ["Account Title:", `IISS ${challanData.items[0].centerName}`],
            ["Branch Name:", challanData.items[0].branchName],
            ["Challan No.:", challanData.items[0].challanNo],
            ["Due Date:", challanData.items[0].dueDate],
          ],
          styles: { ...styles, fontSize: 7.5, border: 1 },
          margin: { left: currentX },
          tableWidth: 260,
        });

        // Footer
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 10,
          body: [
            [
              {
                content: "CASH ONLY\nBank Stamp Here",
                styles: {
                  halign: "center",
                  fontSize: 17.5,
                  fontStyle: "bold",
                  textColor: [225, 225, 225],
                },
              },
            ],
          ],
          styles: { ...styles, border: 0 },
          margin: { left: currentX },
          tableWidth: 260,
        });

        // Note
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 10,
          body: [
            ["This is a system-generated document and needs no signature"],
          ],
          styles: { ...styles, fontSize: 7, border: 0 },
          margin: { left: currentX },
          tableWidth: 260,
        });

        currentX += 270;
      });

      // Open PDF
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newWindow = window.open(pdfUrl, "_blank");
      if (!newWindow) {
        toast.error("Pop-up blocked. Please allow pop-ups.");
      }
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
    } catch (error) {
      console.error("Error:", error);
      toast.error(`Failed to generate challan: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // const handlePrintChallanAction = async (row) => {
  //   if (!row.challanId) {
  //     alert("Error.");
  //     return;
  //   }

  //   // setLoading(true);
  //   try {

  //     const API_BASE_URL = process.env.REACT_APP_API_URL;

  //     const response = await fetch(
  //       `${API_BASE_URL}StudentChallan/GetMonthlyBillingChallanByChallanId?studentFirstRegistrationId=${row.id}&installmentId=${sessionPeriod.value}`,
  //       { method: "GET", headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     if (!response.ok) throw new Error("Network response was not ok");
  //     const blob = await response.blob();
  //     const url = window.URL.createObjectURL(blob);
  //     window.open(url);

  //     // getRegistrationsList();
  //     setTimeout(() => window.URL.revokeObjectURL(url), 100);
  //   } catch (error) {
  //     console.error("Error fetching admission voucher:", error);
  //   } finally {
  //     // setLoading(false);
  //   }

  //   // generatePDF(row.id, sessionPeriod.value);
  // };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Id",
      selector: (row) => row.id,
      omit: true,
    },
    {
      name: "Student",
      selector: (row) => row.name,
      sortable: true,
      width: "120px",
    },
    {
      name: "Installment ID",
      selector: (row) => row.challanMonth,
      sortable: true,
      width: "120px",
    },
    {
      name: "Challan No",
      selector: (row) => row.challanId,
      sortable: true,
    },
    {
      name: "Due Date",
      selector: (row) => row.challanDueDate,
      sortable: true,
      width: "150px",
    },
    {
      name: "Amount",
      selector: (row) => row.totalAmount,
      sortable: true,
    },
    {  
      name: "Status",
      selector: (row) => (row.isPaid == true ? "Paid" : "Un Paid"),
      sortable: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <div>
          {row.challanId ? (
            row.isPaid ? (
              <button
                type="button"
                className="btn py-1 px-0 border-0 custom-disabled-btn"
                data-tooltip-content="Print Challan"
                onClick={() => handlePrintChallanAction(row)}
              >
                <i className="fas fa-print"></i>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn py-1 px-0 border-0 custom-disabled-btn me-3"
                  data-tooltip-content="Print Challan"
                  data-tooltip-id="global-tooltip"
                  onClick={() => handlePrintChallanAction(row)}
                >
                  <i className="fas fa-print"></i>
                </button>
                <button
                  type="button"
                  className="btn py-1 px-0 border-0 custom-disabled-btn"
                  data-tooltip-content="Pay Challan"
                  data-tooltip-id="global-tooltip"
                  onClick={() => handlePayChallanClick(row)}
                >
                  <i className="text-primary fs-9 fas fa-file-invoice-dollar"></i>
                </button>
              </>
            )
          ) : (
            <button
              type="button"
              className="btn py-1 px-0 border-0 custom-disabled-btn"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Process Challan"
              onClick={() => processStudents(row.id)}
            >
              <i className="text-primary fs-9 fas fa-cog"></i>
            </button>
          )}
        </div>
      ),
    },
  ];
  const submitPayChallan = async (formData) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const currentDate = new Date();
    const dateString = currentDate.toISOString();

    const formDataWithPaymentInfo = {
      ...formData,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      paidBy: localStorage.getItem("userId"),
      id: formData.id,
      isPaid: true,
      paymentDate: dateString,
      voucherNo: formData.voucherNo,
      bankName: formData.bankName,
      accountNo: formData.accountNo,
      receiptNo: formData.receiptNo,
      remarks: formData.remarks,
    };
    setProcessing(true);
    try {
      const response = await post(
        `StudentChallan/UpdateMonthlyChallanPaidAsync`,
        formDataWithPaymentInfo,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setShowModal(false);
        getAllStudentByClassIdAndCenterId();
      }
    } catch (error) {
      console.error("Error saving category:", error);
    } finally {
      setProcessing(false);
    }
  };

  const filteredSearchData = billingStudent.filter((item) => {
    const searchLower = searchTerm?.toLowerCase();
    return (
      item.name?.toLowerCase().includes(searchLower) ||
      item.challanId?.toString().toLowerCase().includes(searchLower) ||
      item.challanDueDate?.toLowerCase().includes(searchLower) ||
      item.totalAmount?.toString().toLowerCase().includes(searchLower)
    );
  });

  return (
    <>
      <div className="">
        <div className="page-header d-flex mb-2">
          <h2 className="page-heading fs-7">Periodic Billing</h2>
        </div>

        <div className="row g-3">
          <div className="col-md-5">
            <div className="card">
              <div className="card-body">
                <form className="row g-3 new-form-style">
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Center Session
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name={`sessionId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={sessionsData.map((session) => ({
                            value: session.id,
                            label: session.sessionName,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            field.onChange(selectedOption);
                            if (actionMeta.action === "clear") {
                              setSelectedSessionId(null);
                              setSessionPeriods([]);
                              setValue("sessionPeriod", null);
                              setSelectedPeriodId(null);
                            } else {
                              setSelectedSessionId(selectedOption.value);
                            }
                            trigger(`sessionId`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isDisabled
                        />
                      )}
                    />
                    {errors.sessionId && (
                      <small className="text-danger">
                        {errors.sessionId.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6 d-none">
                    <label className="form-label form-label-new">
                      Center<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="centerId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={centersData.map((center) => ({
                            value: center.id,
                            label: center.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            field.onChange(selectedOption);
                            if (actionMeta.action === "clear") {
                              setValue("centerId", null);
                              setValue("centerClassId", null);
                              setClassesData([]);
                              setSelectedCenterId(null);
                              setSelectedClassId(null);
                              setSections([]);
                            } else {
                              setValue("centerId", selectedOption);
                              setValue("centerClassId", null);
                              setSelectedCenterId(selectedOption.value);
                              setSelectedClassId(null);
                              fetchClassesByCenterId(selectedOption.value);
                            }
                            trigger("centerId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable={!savedCenterId}
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Session Period<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="sessionPeriod"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={sessionPeriods.map((period) => ({
                            value: period.id,
                            label: period.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            field.onChange(selectedOption);
                            if (actionMeta.action === "clear") {
                              setSelectedPeriodId(null);
                            } else {
                              setSelectedPeriodId(selectedOption.value);
                            }
                            trigger("sessionPeriod");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable
                        />
                      )}
                    />
                    {errors.sessionPeriod && (
                      <small className="text-danger">
                        {errors.sessionPeriod.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Class<span className="text-danger fs-9"></span>
                    </label>
                    <Controller
                      name="centerClassId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={classesData.map((classItem) => ({
                            value: classItem.id,
                            label: classItem.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            field.onChange(selectedOption);

                            // Always reset section when Year Group changes or is cleared
                            setValue("sectionId", null);
                            setSelectedSectionId(null);
                            setSections([]);

                            if (actionMeta.action === "clear") {
                              setSelectedClassId(null);
                              setValue("centerClassId", null);
                            } else {
                              setSelectedClassId(selectedOption.value);
                              setValue("centerClassId", selectedOption);
                            }

                            trigger("centerClassId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Section<span className="text-danger fs-9"></span>
                    </label>
                    <Controller
                      name="sectionId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={sections.map((Section) => ({
                            value: Section.id,
                            label: Section.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            field.onChange(selectedOption);
                            if (actionMeta.action === "clear") {
                              setValue("sectionId", null);
                              setSelectedSectionId(null);
                            } else {
                              setValue("sectionId", selectedOption);
                              setSelectedSectionId(selectedOption.value);
                            }
                            trigger("sectionId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable
                        />
                      )}
                    />
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Fee Frequency<span className="text-danger fs-9">*</span>
                    </label>
                    <div className="d-flex gap-2">
                      <div className="w-100">
                        <Controller
                          name="feeFrequency"
                          control={control}
                          rules={{ required: "This field is required." }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={feeFrequencyData.map((frequency) => ({
                                value: frequency.id,
                                label: frequency.value,
                              }))}
                              // REMOVED onChange handler to prevent user changes
                              className="react-select-style"
                              classNamePrefix="react-select-style-pref"
                              isDisabled // ADDED: Make the field disabled
                            />
                          )}
                        />
                        {errors.feeFrequency && (
                          <small className="text-danger">
                            {errors.feeFrequency.message}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mt-3">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={processing}
                      onClick={() => processStudents()}
                    >
                      {processing ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </>
                      ) : (
                        "Process"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-md-7">
            <div className="card">
              <div className="card-body">
                <div
                  className="d-flex justify-content-end"
                  style={{ marginTop: 0 }}
                >
                  <div className="d-flex align-items-start gap-1">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="table-container fixed-last-column-table">
                  <DataTable
                    columns={columns}
                    data={filteredSearchData}
                    pagination
                    dense
                    progressPending={fetching}
                    customStyles={customTableStyles}
                    highlightOnHover={true}
                    onChangePage={setCurrentPage}
                    onChangeRowsPerPage={setRowsPerPage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <PayChallanModal
          title="Pay Challan"
          onClose={() => setShowModal(false)}
          data={modalData}
          payChallan={submitPayChallan}
        />
      )}

      {loading && <FullScreenLoader />}
    </>
  );
}

export default MonthlyBilling;
